import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { navigateWithoutReload } from '@/lib/navigation';
import { ApiAuthError, ApiNetworkError, classifyApiError } from '@/services/apiError';

const API_BASE_URL = resolveApiBaseUrl();
const API_BASE_URL_CONFIG_ERROR = getApiBaseUrlConfigError();

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    if (API_BASE_URL_CONFIG_ERROR) {
      throw new ApiNetworkError(API_BASE_URL_CONFIG_ERROR, {
        code: 'API_BASE_URL_NOT_CONFIGURED',
        statusCode: 503,
        details: {
          requestedPath: config.url,
        },
      });
    }

    const token = useAuthStore.getState().token ?? localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (!localStorage.getItem('token')) {
        localStorage.setItem('token', token);
      }
    }
    return config;
  },
  (error) => Promise.reject(classifyApiError(error)),
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestUrl = error.config?.url;
    const normalizedError = classifyApiError(error);

    if (normalizedError instanceof ApiAuthError) {
      if (normalizedError.statusCode === 401 && !isAuthRequest(requestUrl)) {
        const { token, isAuthenticated, clearAuth } = useAuthStore.getState();
        const hasPersistedToken = Boolean(localStorage.getItem('token'));
        if (token || isAuthenticated || hasPersistedToken) {
          clearAuth();
          redirectIfNeeded('/401');
        }
      }

      if (normalizedError.statusCode === 403) {
        redirectIfNeeded('/403');
      }
    }

    return Promise.reject(normalizedError);
  },
);

function isAuthRequest(url?: string) {
  if (!url) {
    return false;
  }

  const normalized = url.toLowerCase();
  return normalized.includes('/auth/login') || normalized.includes('/auth/register');
}

function redirectIfNeeded(targetPath: '/401' | '/403') {
  navigateWithoutReload(targetPath);
}

function resolveApiBaseUrl() {
  const configuredBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);

  if (configuredBaseUrl) {
    return configuredBaseUrl;
  }

  return '/api';
}

function getApiBaseUrlConfigError() {
  if (normalizeApiBaseUrl(import.meta.env.VITE_API_URL)) {
    return null;
  }

  if (isSameOriginApiAllowed()) {
    return null;
  }

  return 'Backend API URL is not configured. Set VITE_API_URL to your backend /api origin.';
}

function isSameOriginApiAllowed() {
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
    return true;
  }

  return import.meta.env.VITE_API_USE_SAME_ORIGIN === 'true';
}

function normalizeApiBaseUrl(value: string | undefined) {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.replace(/\/$/, '');
}

export default api;
